import React, { useEffect, useRef } from 'react'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import { formLabelCss } from '../../legacy-design-system/text'
import { CodeBlock } from '@contrast-security-inc/design-system-surfaces'
import { css } from '@emotion/css'
import { useAddAgentAuditLog } from './api/use-agent-logs'

export interface FieldProps {
  readonly label: MessageDescriptor
  readonly value?: string
  readonly description?: React.ReactNode
  readonly inlineDescription?: boolean
  readonly className?: string
  readonly contrastScrapeID?: string
  readonly testId?: string
  readonly limitedCodeBlock?: boolean
  readonly isObfuscated?: boolean
  readonly showObfuscationButton?: boolean
  readonly orgUuid?: string
  readonly agentUid?: string | null
  readonly valueType?: AgentKeyType
}

export function Field({
  label,
  value,
  description,
  className,
  contrastScrapeID,
  testId,
  limitedCodeBlock,
  isObfuscated,
  showObfuscationButton,
  orgUuid,
  agentUid = null,
  valueType
}: FieldProps) {
  const fieldclass = css`
    code {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
      width: 37vw;
    }
    width: 40vw;
  `

  const { mutate } = useAddAgentAuditLog()

  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!orgUuid || !valueType) {
      return
    }

    const wrapper = wrapperRef.current

    const handleViewClick = () => {
      mutate({
        orgUuid,
        agentUid,
        valueType,
        action: AgentKeyAction.View
      })
    }

    const handleCopyClick = () => {
      mutate({
        orgUuid,
        agentUid,
        valueType,
        action: AgentKeyAction.Copy
      })
    }

    if (wrapper && testId) {
      const viewButton = wrapper.querySelector(
        `button[data-testid="${testId}-code-block-view-button"]`
      )
      const copyButton = wrapper.querySelector(
        `button[data-testid="${testId}-code-block-copy-button"]`
      )

      if (viewButton) {
        viewButton.addEventListener('click', handleViewClick)
      }
      if (copyButton) {
        copyButton.addEventListener('click', handleCopyClick)
      }

      return () => {
        if (viewButton) {
          viewButton.removeEventListener('click', handleViewClick)
        }
        if (copyButton) {
          copyButton.removeEventListener('click', handleCopyClick)
        }
      }
    }
  }, [orgUuid, agentUid, valueType, testId])

  return (
    <div className={className} id={`${label.id}-id`} ref={wrapperRef}>
      <FormattedMessage {...label}>
        {(text) => <div className={formLabelCss}>{text}</div>}
      </FormattedMessage>
      {value && (
        <CodeBlock
          isObfuscated={isObfuscated}
          showObfuscationButton={showObfuscationButton}
          className={limitedCodeBlock ? fieldclass : ''}
          aria-errormessage={`${label.id}-error`}
          data-e2e={contrastScrapeID}
          message={value}
          isSelectable={false}
          testId={`${testId}-code-block`}
        />
      )}
      <div>{description}</div>
    </div>
  )
}

export enum AgentKeyType {
  Token = 'TOKEN',
  ApiKey = 'API_KEY',
  ServiceKey = 'SERVICE_KEY',
  Username = 'USERNAME'
}

export enum AgentKeyAction {
  Copy = 'COPY',
  View = 'VIEW'
}
